
import { Header } from "@/components/learn-more/Header";
import { IntroSection } from "@/components/learn-more/IntroSection";
import { TargetUsersSection } from "@/components/learn-more/TargetUsersSection";
import { HowItWorksSection } from "@/components/learn-more/HowItWorksSection";
import { GetStartedSection } from "@/components/learn-more/GetStartedSection";
import { Footer } from "@/components/learn-more/Footer";
import { SEO } from "@/components/common/SEO";
import { CoreFeaturesSection } from "@/components/features/CoreFeaturesSection";
import { FeatureScreenshotSection } from "@/components/features/FeatureScreenshotSection";
import { FeatureShowcase } from "@/components/learn-more/FeatureShowcase";
import { useIsMobile } from "@/hooks/use-mobile";

export default function LearnMore() {
  const isMobile = useIsMobile();
  
  // Structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Learn More About Hour Tracker - Time Tracking Software for Any Workflow",
    "description": "Discover how Hour Tracker's workflow-optimized time tracking software helps freelancers, consultants, and small businesses track billable hours and generate invoices.",
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "headline": "Learn More About Hour Tracker - Time Tracking Software for Any Workflow"
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Learn More About Hour Tracker | Time Tracking Software for Any Workflow" 
        description="Discover how Hour Tracker's workflow-optimized time tracking software helps freelancers, consultants, and small businesses track billable hours and generate invoices with ease."
        keywords="time tracking software, workflow optimization, freelance invoice tool, project management, billable hours, productivity tool, small business software, any workflow"
        ogType="article"
        structuredData={structuredData}
      />
      
      <Header />

      <main className="flex-1">
        <IntroSection />
        
        {/* Core features with icons - Moved up */}
        <CoreFeaturesSection />
        
        {/* Featured Screenshots Grid - Showcase the app */}
        <FeatureShowcase />
        
        {/* Detailed feature sections with alternating screenshots */}
        <div className="bg-gradient-to-b from-white to-muted/10">
          <FeatureScreenshotSection 
            title="Workflow-Optimized Time Tracking"
            description="Log your hours with precision and ease, designed to fit any workflow. Track by date, project, and client with detailed descriptions for each entry. Edit historical entries whenever needed to ensure accuracy in your billing."
            imageSrc="/lovable-uploads/786bc681-3b49-4efa-9e0d-1c81bf8ca3b8.png"
            imageAlt="Hour Tracker time tracking interface"
          />
          
          <FeatureScreenshotSection 
            title="Flexible Project Management"
            description="Create unlimited projects that adapt to your workflow with custom hourly rates. Categorize by client and track progress at a glance. Easily archive completed projects to keep your workspace clutter-free."
            imageSrc="/lovable-uploads/ce11ceb3-7e02-457d-b7f5-00aba710bac5.png"
            imageAlt="Hour Tracker project management interface"
            reverse={true}
          />
          
          <FeatureScreenshotSection 
            title="Professional Invoice Generation"
            description="Turn your tracked time into professional invoices with one click, regardless of your workflow. Include detailed time breakdowns and monthly summaries. Download and share invoice files directly with your clients."
            imageSrc="/lovable-uploads/126458a5-a531-4d94-8fe3-ed8be78fe7a6.png"
            imageAlt="Hour Tracker invoice generation interface"
          />
          
          <FeatureScreenshotSection 
            title="Workflow Analytics & Insights"
            description="Visualize your productivity with interactive charts and detailed reports. Track earnings by project, analyze time distribution, and identify workflow optimization opportunities to maximize your efficiency."
            imageSrc="/lovable-uploads/087e1512-f500-4386-b0d3-e438b1a28848.png"
            imageAlt="Hour Tracker analytics interface"
            reverse={true}
          />
        </div>
        
        <TargetUsersSection />
        
        <HowItWorksSection />
        
        <GetStartedSection />
      </main>

      <Footer />
    </div>
  );
}
